import React, {useMemo, useState} from 'react';
import QuantityInput from "./components/quantity-input";
import CurrencyInput from "./components/currency-input";
import './data-table.css';
import Tooltip from "../tooltip/tooltip";

const DataTable = ({
    columns,
    data,
    className = '',
    onRowClick,
    isLoading = false,
    enableRowEdit = false,
    editMode = 'inline',
    onSaveRow = null,
    onOpenModal = null,
    variant = 'default',
    pagination = null,
    onSort = null,
}) => {
    const [editingRowId, setEditingRowId] = useState(null);
    const [editedRow, setEditedRow] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [sortConfig, setSortConfig] = useState({ accessor: null, direction: null });
    const [hoveredRow, setHoveredRow] = useState(null);

    const parseCurrency = (val) => {
        if (val == null) return 0;
        if (typeof val === 'number') return val;
        let cleaned = String(val).replace(/[^0-9,.\-]/g, '');
        if (!cleaned) return 0;
        if (cleaned.includes(',') && cleaned.includes('.')) cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        else if (cleaned.includes(',')) cleaned = cleaned.replace(',', '.');
        const n = parseFloat(cleaned);
        return isNaN(n) ? 0 : n;
    };

    const cancelEdit = () => {
        setEditingRowId(null);
        setEditedRow({});
        setIsSaving(false);
    };

    const handleChange = (accessor, value) => {
        setEditedRow((prev) => ({ ...prev, [accessor]: value }));
    };

    const handleSave = async () => {
        if (!onSaveRow) return cancelEdit();
        try {
            setIsSaving(true);
            const clean = { ...editedRow };
            columns.forEach((col) => {
                const acc = col.accessor;
                if (col.type === 'currency') clean[acc] = parseCurrency(clean[acc]);
                else if (col.type === 'quantity' || col.inputType === 'number') clean[acc] = Number(clean[acc]);
            });
            await onSaveRow(clean);
            setEditingRowId(null);
            setEditedRow({});
            setIsSaving(false);
        } catch (err) {
            console.error(err);
            setIsSaving(false);
        }
    };

    const renderCellContent = (col, row) => {
        const accessor = col.accessor;
        const isEditingThisRow = editingRowId === row.id;

        if (isEditingThisRow && editMode === 'inline' && col.editable) {
            const value = editedRow[accessor] ?? '';

            if (col.type === 'quantity') return <QuantityInput value={value} onChange={(v) => handleChange(accessor, v)} />;
            if (col.type === 'currency')
                return <CurrencyInput value={value} onChange={(v) => handleChange(accessor, v)} onEnter={handleSave} onEscape={cancelEdit} />;

            const inputType = col.inputType || (typeof row[accessor] === 'number' ? 'number' : 'text');
            return (
                <input
                    className="datatable-input"
                    type={inputType}
                    value={value}
                    onChange={(e) => handleChange(accessor, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSave();
                        if (e.key === 'Escape') cancelEdit();
                    }}
                />
            );
        }

        return col.renderCell ? col.renderCell(row) : row[accessor];
    };

    const handleEditClick = (row) => {
        const canEdit = typeof enableRowEdit === 'function' ? enableRowEdit(row) : enableRowEdit;
        if (!canEdit) return;

        if (editMode === 'modal') {
            if (onOpenModal) onOpenModal(row);
        } else {
            if (editingRowId === row.id) {
                cancelEdit();
                return;
            }

            setEditingRowId(row.id);
            const clone = { ...row };
            columns.forEach((c) => {
                const acc = c.accessor;
                if (c.type === 'currency') clone[acc] = parseCurrency(clone[acc]);
                else if (c.type === 'quantity' || c.inputType === 'number') clone[acc] = Number(clone[acc] || 0);
            });
            setEditedRow(clone);
        }
    };

    const handleSort = (col) => {
        if (!col.sortable) return;

        let direction = 'asc';
        if (sortConfig.accessor === col.accessor && sortConfig.direction === 'asc') {
            direction = 'desc';
        } else if (sortConfig.accessor === col.accessor && sortConfig.direction === 'desc') {
            direction = null;
        }

        const newSort = {accessor: col.accessor, direction};
        setSortConfig(newSort);
        if (onSort) onSort(newSort);
    };

    const columnLayout = useMemo(() => {
        const columnTemplates = columns.map(col => col.width || 'minmax(0, 1fr)').join(' ');
        const actionsColumn = enableRowEdit ? ' auto' : '';
        return `${columnTemplates}${actionsColumn}`;
    }, [columns, enableRowEdit]);

    const dynamicStyles = {'--datatable-columns-layout': columnLayout};

    const totalItems = pagination?.count || data.length;
    const currentPage = pagination?.currentPage || 1;
    const hasNext = Boolean(pagination?.next);
    const hasPrevious = Boolean(pagination?.previous);

    const startIndex = (currentPage - 1) * 10 + 1;
    const endIndex = Math.min(currentPage * 10, totalItems);

    return (
        <>
            <div className="d-flex flex-column gap-3">
                <div className={`datatable-container ${className} ${variant}`} style={dynamicStyles}>
                    <div className="datatable-header">
                        <div className="datatable-header-row">
                            {columns.map((col, idx) => {
                                const isSorted = sortConfig.accessor === col.accessor;
                                const direction = isSorted ? sortConfig.direction : null;
                                return (
                                    <div
                                        key={idx}
                                        className={`datatable-header-cell ${typeof col.className === "object" ? col.className.header : col.className || ""} ${col.sortable ? "sortable gap-0" : ""}`}
                                        title={col.header}
                                        onClick={() => handleSort(col)}
                                        style={{cursor: col.sortable ? 'pointer' : 'default'}}
                                    >
                                        <span>{col.header}</span>
                                        {col.sortable && (
                                            <div className={`sort-icon mt-1 ${direction ? "visible" : ""}`}>
                                                {direction === 'asc' && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                         viewBox="0 0 24 24" fill="none">
                                                        <path
                                                            d="M12.1921 9.23047L15.9065 13.6879C16.3408 14.2089 15.9702 15 15.292 15H8.70803C8.02976 15 7.65924 14.2089 8.09346 13.6879L11.8079 9.23047C11.9079 9.11047 12.0921 9.11047 12.1921 9.23047Z"
                                                            fill="#222222"/>
                                                    </svg>
                                                )}
                                                {direction === 'desc' && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                         viewBox="0 0 24 24" fill="none">
                                                        <path
                                                            d="M11.8079 14.7695L8.09346 10.3121C7.65924 9.79109 8.02976 9 8.70803 9L15.292 9C15.9702 9 16.3408 9.79108 15.9065 10.3121L12.1921 14.7695C12.0921 14.8895 11.9079 14.8895 11.8079 14.7695Z"
                                                            fill="#222222"/>
                                                    </svg>
                                                )}
                                                {!direction && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                         viewBox="0 0 24 24" fill="none">
                                                        <path
                                                            d="M12.1921 4.23047L15.9065 8.68794C16.3408 9.20891 15.9702 10 15.292 10H8.70803C8.02976 10 7.65924 9.20891 8.09346 8.68794L11.8079 4.23047C11.9079 4.11047 12.0921 4.11047 12.1921 4.23047Z"
                                                            fill="#222222"/>
                                                        <path
                                                            d="M11.8079 19.7695L8.09346 15.3121C7.65924 14.7911 8.02976 14 8.70803 14H15.292C15.9702 14 16.3408 14.7911 15.9065 15.3121L12.1921 19.7695C12.0921 19.8895 11.9079 19.8895 11.8079 19.7695Z"
                                                            fill="#222222"/>
                                                    </svg>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {enableRowEdit && <div className="datatable-header-cell actions"></div>}
                        </div>
                    </div>

                    <div className={`datatable-body ${variant}-body`}>
                        {
                            isLoading ? (
                                Array.from({length: 3}).map((_, rowIndex) => (
                                    <div key={rowIndex} className={`datatable-row text-truncate ${variant}-row`}>
                                        {columns.map((col, colIndex) => (
                                            <div key={colIndex}
                                                 className={`datatable-cell skeleton ${col.className || ''}`}/>
                                        ))}
                                        {enableRowEdit && <div className="datatable-cell skeleton"/>}
                                    </div>
                                ))
                            ) : data.length === 0 ? (
                                <div className="datatable-row no-data">
                                    <div className="datatable-cell no-data-cell">
                                        No hay datos disponibles
                                    </div>
                                </div>
                            ) : (
                                data.map((row, rowIndex) => {
                                    const canEdit = typeof enableRowEdit === 'function' ? enableRowEdit(row) : enableRowEdit;
                                    return (
                                        <div
                                            key={rowIndex}
                                            className={`datatable-row text-truncate ${variant}-row`}
                                            onClick={() => onRowClick && onRowClick(row)}
                                            onMouseEnter={() => setHoveredRow(rowIndex)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                        >
                                            {columns.map((col, colIndex) => (
                                                <div
                                                    key={colIndex}
                                                    className={`datatable-cell text-truncate ${typeof col.className === "object" ? col.className.cell : col.className || ""} ${hoveredRow === rowIndex ? "expand" : ""}`}
                                                >
                                                    {renderCellContent(col, row)}
                                                </div>
                                            ))}
                                            {enableRowEdit && (
                                                <div
                                                    className="datatable-cell actions d-flex align-items-center justify-content-center p-0">
                                                    <Tooltip title="Editar" position="top">
                                                        <button
                                                            className="btn btn-sm btn-link p-0"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditClick(row);
                                                            }}
                                                            disabled={!canEdit}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18"
                                                                 height="18"
                                                                 viewBox="0 0 16 16" fill="none">
                                                                <path
                                                                    d="M6.54089 15.6515C4.96338 15.6515 3.38562 15.652 1.80811 15.6515C0.727173 15.651 0.000488281 14.9388 0.000366211 13.879C-0.00012207 10.776 -0.00012207 7.67444 0.000366211 4.5719C0.000366211 3.50964 0.725952 2.79822 1.80725 2.79798C4.2981 2.79773 6.78906 2.79675 9.28015 2.80115C9.42297 2.80115 9.51978 2.75867 9.61938 2.66077C10.3237 1.96082 11.0342 1.26648 11.7435 0.571657C12.5199 -0.18933 13.531 -0.19055 14.3115 0.567995C14.6698 0.916139 15.0259 1.26624 15.3811 1.61731C16.1372 2.36609 16.1366 3.36756 15.3798 4.11389C14.6641 4.81824 13.9479 5.52356 13.2264 6.22327C13.1292 6.31751 13.0948 6.41199 13.095 6.54334C13.0988 9.00574 13.099 11.4674 13.097 13.9298C13.0964 14.7416 12.5959 15.3956 11.8188 15.5929C11.6495 15.6361 11.4679 15.649 11.2915 15.6498C9.70789 15.6534 8.12439 15.6515 6.54089 15.6515ZM11.9578 7.4845C11.884 7.55261 11.8374 7.59339 11.7927 7.63709C10.3794 9.02356 8.96863 10.413 7.54883 11.7931C7.42529 11.9135 7.25146 12.0111 7.08362 12.0546C5.76721 12.3976 4.44678 12.725 3.12695 13.0546C3.0365 13.0775 2.93774 13.0941 2.84644 13.0839C2.45483 13.0389 2.24854 12.683 2.38647 12.2889C2.80872 11.0792 3.23083 9.87 3.66418 8.66467C3.71912 8.51184 3.8147 8.35877 3.93079 8.24402C5.33875 6.85169 6.75342 5.46643 8.16675 4.07947C8.21118 4.03626 8.2533 3.99036 8.32361 3.91663H8.11877C6.01343 3.91663 3.90833 3.91663 1.80273 3.91712C1.35425 3.91712 1.1405 4.12879 1.1405 4.5719C1.14026 7.67444 1.14026 10.776 1.1405 13.8785C1.1405 14.3175 1.35791 14.5326 1.80408 14.5326C4.96545 14.5326 8.1261 14.5326 11.2871 14.5326C11.7457 14.5326 11.9572 14.3226 11.9573 13.8673C11.9578 11.8068 11.9578 9.74646 11.9578 7.6864C11.9578 7.63098 11.9578 7.57532 11.9578 7.4845ZM11.4248 2.55579C9.3053 4.63538 7.17395 6.72693 5.05554 8.8053C5.69263 9.43176 6.34546 10.0721 6.97583 10.692C9.09985 8.60755 11.2311 6.51575 13.3448 4.44177C12.7073 3.81482 12.0551 3.17468 11.4248 2.55579ZM4.43628 9.90344C4.22986 10.4928 4.02014 11.0897 3.79712 11.7262C4.49048 11.5519 5.14233 11.3881 5.78345 11.2272C5.32788 10.7799 4.88684 10.3463 4.43628 9.90344Z"
                                                                    fill="#9DACBD"/>
                                                            </svg>
                                                        </button>
                                                    </Tooltip>

                                                    {editingRowId === row.id && (
                                                        <Tooltip title="Guardar" position="left">
                                                            <button
                                                                className="btn btn-sm btn-link p-0"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSave();
                                                                }}
                                                                disabled={isSaving}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="18"
                                                                     height="18"
                                                                     viewBox="0 0 19 19" fill="none">
                                                                    <circle cx="9.5" cy="9.5" r="9.5" fill="#3177BE"/>
                                                                    <g transform="translate(3,3)">
                                                                        <path
                                                                            d="M10.2917 11.375H2.70833C2.42102 11.375 2.14547 11.2609 1.9423 11.0577C1.73914 10.8545 1.625 10.579 1.625 10.2917V2.70833C1.625 2.42102 1.73914 2.14547 1.9423 1.9423C2.14547 1.73914 2.42102 1.625 2.70833 1.625H8.66667L11.375 4.33333V10.2917C11.375 10.579 11.2609 10.8545 11.0577 11.0577C10.8545 11.2609 10.579 11.375 10.2917 11.375Z"
                                                                            stroke="white" strokeWidth="1.08333"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"/>
                                                                        <path
                                                                            d="M9.20964 11.3743V7.04102H3.79297V11.3743"
                                                                            stroke="white" strokeWidth="1.08333"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"/>
                                                                        <path d="M3.79297 1.625V4.33333H8.1263"
                                                                              stroke="white"
                                                                              strokeWidth="1.08333"
                                                                              strokeLinecap="round"
                                                                              strokeLinejoin="round"/>
                                                                    </g>
                                                                </svg>
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )
                        }
                    </div>
                </div>

                {pagination && (
                    <div className="d-flex justify-content-end align-items-center gap-4 w-75 ms-auto">
                            <span className="plans-list-panel-table-labels">
                        {totalItems > 0 ? `${startIndex} - ${endIndex} de ${totalItems}` : "0 de 0"}
                    </span>
                        <div className="d-flex gap-2">
                            <svg
                                className={`bi bi-chevron-left ${
                                    !hasPrevious ? "text-muted" : "cursor-pointer"
                                }`}
                                onClick={hasPrevious ? pagination.onPrevious : undefined}
                                xmlns="http://www.w3.org/2000/svg"
                                width="9"
                                height="15"
                                viewBox="0 0 9 15"
                                fill="none"
                                style={{pointerEvents: !hasPrevious ? "none" : "auto"}}
                            >
                                <path
                                    d="M0.0423059 7.30351C0.0423059 6.92015 0.188535 6.53675 0.481072 6.24419L6.28647 0.438753C6.87147 -0.146251 7.81998 -0.146251 8.405 0.438753C8.99 1.02376 8.99 1.97231 8.405 2.55733L3.65881 7.30351L8.405 12.0497C8.99 12.6347 8.99 13.5832 8.405 14.1683C7.81998 14.7533 6.87147 14.7533 6.28647 14.1683L0.481072 8.36282C0.188535 8.0703 0.0423059 7.68685 0.0423059 7.30351Z"
                                    fill="#9DACBD"
                                />
                            </svg>

                            <svg
                                className={`bi bi-chevron-right ${
                                    !hasNext ? "text-muted" : "cursor-pointer"
                                }`}
                                onClick={hasNext ? pagination.onNext : undefined}
                                xmlns="http://www.w3.org/2000/svg"
                                width="10"
                                height="15"
                                viewBox="0 0 10 15"
                                fill="none"
                                style={{pointerEvents: !hasNext ? "none" : "auto"}}
                            >
                                <path
                                    d="M9.67644 7.30196C9.67644 7.68532 9.53022 8.06872 9.23768 8.36128L3.43228 14.1667C2.84728 14.7517 1.89877 14.7517 1.31375 14.1667C0.72875 13.5817 0.72875 12.6332 1.31375 12.0481L6.05994 7.30196L1.31375 2.55578C0.72875 1.97078 0.72875 1.02223 1.31375 0.437193C1.89877 -0.14781 2.84728 -0.14781 3.43228 0.437193L9.23768 6.24264C9.53022 6.53517 9.67644 6.91862 9.67644 7.30196Z"
                                    fill="#9DACBD"
                                />
                            </svg>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default DataTable;
